import { createSignal, onMount, Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Button } from '../../ui-components/button/button';
import { Link } from '../../ui-components/link/link';
import { useLocation, useNavigate } from '@solidjs/router';
import { logIn, acceptPrivacyPolicy } from '../../tools/login-user';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { fieldShellClasses, FormFieldLabel } from '../forms-tw/forms-fields/primitives';
import { cviSemantic } from '../../style/theme';
import { handleResendVerification } from '../../tools/verify-user';
import { PrivacyPolicyModal } from '../privacy-policy-modal/privacy-policy-modal';

type LoginFormProps = { 
    firstElement: (el: any) => void
    lastElement: (el: any) => void
};

export const LoginForm = (props: LoginFormProps) => {

    let emailInputRef: HTMLInputElement | undefined;
    const {
        appStore,
        pageRegistration,
        localize,
        pageRequestPasswordResetUrl,
        updateUser,
        recaptchaSiteKey,
        deploymentEnvironment,
        ssoBaseUrl,
        graphQlClient,
        siteInfo,
        termsAndConditions,
    } = useContext(AppContext);
    const { closeLoginMenu } = appStore;
    const [emailValue, setEmailValue] = createSignal('');
    const [passwordValue, setPasswordValue] = createSignal('');
    const [errorMessage, setErrorMessage] = createSignal('');
    const [successMessage, setSuccessMessage] = createSignal('');
    const [showResendVerification, setShowResendVerification] = createSignal(false);
    const [resendingVerification, setResendingVerification] = createSignal(false);
    const [verificationResent, setVerificationResent] = createSignal(false);
    const [signingIn, setSigningIn] = createSignal(false);
    const [showPolicyModal, setShowPolicyModal] = createSignal(false);
    const [policyAcceptLoading, setPolicyAcceptLoading] = createSignal(false);
    const [pendingPolicyData, setPendingPolicyData] = createSignal<{
        uuid: string;
        firstName: string;
        userEmail: string;
        siteCountry: string;   // Site's country slug (e.g., "sweden")
        countryCode: string;   // ISO country code (e.g., "SE")
    } | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    onMount(() => {
        if (!document.querySelector('#grecaptcha')) {
            const scriptTag = document.createElement('script');
            scriptTag.type = 'text/javascript';
            scriptTag.id = 'grecaptcha';
            scriptTag.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSiteKey}`;
            document.head.appendChild(scriptTag);
        }

        if (emailInputRef) {
            emailInputRef.focus();
        }
    });

    const handleLogin = async () => {
        const args = {
            email: emailValue(),
            password: passwordValue(),
            siteCountry: siteInfo.country,  // Pass current site's country slug for policy check
        };

        setSigningIn(true);
        const { success, message, policyRequired } = await logIn(args, updateUser, recaptchaSiteKey, deploymentEnvironment, ssoBaseUrl);

        setSigningIn(false);
        setShowResendVerification(false);
        setVerificationResent(false);

        if (!success) {
            if (message === 'user-not-found') {
                setErrorMessage(localize('login-user-not-found', 'No user exists with this email'));
            } else if (message === 'invalid-credentials') {
                setErrorMessage(localize('invalid-credentials', 'Invalid credentials'));
            } else if (message === 'user-not-verified') {
                setErrorMessage(localize('user-not-verified', 'Your account is not verified yet. Please click the button below and then follow the verification link sent to your email to activate your account.'));
                setShowResendVerification(true);
            } else if (message === 'policy-required' && policyRequired) {
                // User needs to accept privacy policy for this site
                // But only show modal if ACF Terms & Conditions is configured
                const isTermsConfigured = !!(termsAndConditions?.privacyPolicy?.content && termsAndConditions.privacyPolicy.content.trim() !== '');

                if (isTermsConfigured) {
                    setPendingPolicyData(policyRequired);
                    setShowPolicyModal(true);
                } else {
                    // ACF not configured - auto-accept and continue login
                    setPolicyAcceptLoading(true);
                    const { success } = await acceptPrivacyPolicy(
                        policyRequired.uuid,
                        policyRequired.countryCode,
                        updateUser,
                        deploymentEnvironment,
                        ssoBaseUrl
                    );
                    setPolicyAcceptLoading(false);

                    if (success) {
                        closeLoginMenu();
                        return;
                    }
                    // If auto-accept failed, show generic error
                    setErrorMessage(localize('error-has-occurred', 'An error has occurred, please try again later'));
                }
            } else {
                setErrorMessage(localize('error-has-occurred', 'An error has occurred, please try again later'));
            }

            return;
        }

        closeLoginMenu();
    };

    const handlePolicyAccept = async () => {
        const policyData = pendingPolicyData();
        if (!policyData) return;

        setPolicyAcceptLoading(true);

        // Accept privacy policy using country code (not site slug)
        const { success } = await acceptPrivacyPolicy(
            policyData.uuid,
            policyData.countryCode,
            updateUser,
            deploymentEnvironment,
            ssoBaseUrl
        );

        setPolicyAcceptLoading(false);

        if (success) {
            setShowPolicyModal(false);
            setPendingPolicyData(null);
            closeLoginMenu();
        } else {
            setShowPolicyModal(false);
            setPendingPolicyData(null);
            setErrorMessage(localize('error-accepting-policy', 'An error occurred while accepting the policy. Please try again.'));
        }
    };

    const handlePolicyModalClose = () => {
        setShowPolicyModal(false);
        setPendingPolicyData(null);
    };

    const handleResendVerificationOnclick = async () => {
        setResendingVerification(true);

        try {
            const success =  await handleResendVerification(emailValue(), graphQlClient, location.pathname);
            if (success) {
                setSuccessMessage(localize('verification-email-sent', 'Verification email sent! Please check your inbox.'));
            } else {
                throw new Error();
            }
        } catch (e) {
            setErrorMessage(localize('error-has-occurred', 'An error has occurred, please try again later'));
        } finally {
            setErrorMessage('');
            setShowResendVerification(false);
            setVerificationResent(true);
            setResendingVerification(false);
        }
    };

    return (
        <div class="pt-cvi-9 pb-cvi-15 tablet:px-cvi-8">
            <form
                class="mx-auto w-full max-w-cvi-form-column"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleLogin();
                }}
            >
                <Show when={errorMessage()}>
                    <div class="mb-cvi-8 body-sm font-cvi-regular text-cvi-fg-danger">
                        {errorMessage()}
                    </div>
                </Show>

                <Show when={successMessage()}>
                    <div class="mb-cvi-8 body-sm font-cvi-regular text-cvi-fg-default">
                        {successMessage()}
                    </div>
                </Show>

                <div class="flex flex-col gap-cvi-7">
                    <div>
                        <FormFieldLabel for="login-email">
                            {localize('email', 'Email')}
                        </FormFieldLabel>

                        <input
                            id="login-email"
                            ref={(el: HTMLElement) => {
                                emailInputRef = el as HTMLInputElement;
                                props.firstElement(el);
                            }}
                            value={emailValue()}
                            onInput={(e) => setEmailValue(e.currentTarget.value)}
                            type="email"
                            name="email"
                            placeholder={localize('email', 'Email')}
                            autocomplete="email"
                            class={fieldShellClasses()}
                        />
                    </div>

                    <div>
                        <FormFieldLabel for="login-password">
                            {localize('password', 'Password')}
                        </FormFieldLabel>

                        <input
                            id="login-password"
                            value={passwordValue()}
                            onInput={(e) => setPasswordValue(e.currentTarget.value)}
                            type="password"
                            name="password"
                            placeholder={localize('password', 'Password')}
                            autocomplete="current-password"
                            class={fieldShellClasses()}
                        />

                        {/* DS puts the recovery link directly under the password field, 8px below. */}
                        <div class="pt-cvi-4">
                            <Link
                                label={localize('reset-password', 'Reset password')}
                                url={pageRequestPasswordResetUrl}
                                noBlockSpacing={true}
                                onClick={() => closeLoginMenu()}
                            />
                        </div>
                    </div>
                </div>

                <Show when={showResendVerification()}>
                    <div class="mt-cvi-10">
                        <Button
                            label={localize('resend-verification-email', 'Resend verification email')}
                            onClick={handleResendVerificationOnclick}
                            disabled={resendingVerification()}
                            noBlockSpacing={true}
                            customCss="width: 100%"
                        />
                    </div>

                    <Show when={resendingVerification()}>
                        <div class="mt-cvi-6 flex justify-center">
                            <LoadingSpinner color={cviSemantic.fgBrand} />
                        </div>
                    </Show>
                </Show>

                <Show when={!showResendVerification() && !verificationResent()}>
                    <div class="mt-cvi-10">
                        <Button
                            label={localize('sign-in', 'Sign in')}
                            type="submit"
                            disabled={signingIn() || emailValue() === '' || passwordValue() === ''}
                            noBlockSpacing={true}
                            customCss="width: 100%"
                        />
                    </div>

                    <Show when={signingIn()}>
                        <div class="mt-cvi-6 flex justify-center">
                            <LoadingSpinner color={cviSemantic.fgBrand} />
                        </div>
                    </Show>

                    <div class="mt-cvi-7">
                        <Button
                            variant="ghost"
                            label={localize('register-a-new-user-account', 'Register a new user account')}
                            element={(el: HTMLElement) => props.lastElement(el)}
                            noBlockSpacing={true}
                            customCss="width: 100%"
                            onClick={() => {
                                navigate(pageRegistration.url, { state: { fromUrl: location.pathname } });
                                closeLoginMenu();
                            }}
                        />
                    </div>
                </Show>
            </form>

            <PrivacyPolicyModal
                visible={showPolicyModal()}
                onClose={handlePolicyModalClose}
                onAccept={() => { handlePolicyAccept(); }}
                loading={policyAcceptLoading()}
                userCountryCode={pendingPolicyData()?.countryCode}
                title={termsAndConditions?.privacyPolicy?.title || localize('policy-acceptance-required', 'Privacy Policy Acceptance Required')}
                description={localize(
                    'policy-acceptance-description',
                    'To continue, you must accept the privacy policy for this site.'
                )}
                contentType="privacy"
            />
        </div>
    );
};

